
import React from 'react';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { format } from "date-fns";
import { PaymentType } from '../../../types/payment';
import StatusBadge from './StatusBadge';

interface PaymentsTableProps {
  payments: PaymentType[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  isAdmin: boolean;
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
  };
  onPageChange: (page: number) => void;
}

const PaymentsTable: React.FC<PaymentsTableProps> = ({
  payments,
  onApprove,
  onReject,
  isAdmin,
  pagination,
  onPageChange
}) => {
  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableCaption>
            {payments.length === 0 
              ? "No payments found matching your filters" 
              : "List of referrer payments"}
          </TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Referrer ID</TableHead>
              <TableHead>Referrer Name</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Status</TableHead>
              {isAdmin && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 7 : 6} className="h-24 text-center">
                  No payments found
                </TableCell>
              </TableRow>
            ) : (
              payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{format(new Date(payment.date), "MMM d, yyyy")}</TableCell>
                  <TableCell className="font-medium">{payment.referrerId}</TableCell>
                  <TableCell>{payment.referrerName}</TableCell>
                  <TableCell>${payment.amount.toFixed(2)}</TableCell>
                  <TableCell>
                    <span className="capitalize">
                      {payment.method.replace('_', ' ')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={payment.status} />
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      {payment.status === 'pending' && (
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onApprove(payment.id)}
                            className="h-8 w-8 p-0 text-green-600"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onReject(payment.id)}
                            className="h-8 w-8 p-0 text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-end mt-4 space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            Previous
          </Button>
          <span className="py-2 px-1">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default PaymentsTable;
